/**
 * Time tracking data — DESIGN.md §6.13.
 *
 * The running timer is server-side (§6.13), so it is a query rather than local
 * state: it survives a reload, and a timer started on a laptop shows in the
 * header chip on a phone. It polls on a minute, because the elapsed display
 * only changes that often and SSE already pushes the start/stop transitions.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  formatDuration,
  type ActiveTimer,
  type CreateTimeEntryRequest,
  type TaskTimeSummary,
  type TimeEntry,
  type UpdateTimeEntryRequest,
} from '@slate/shared';
import { http } from '@/lib/api';
import { queryKeys } from '@/lib/keys';

export const timerKey = ['me', 'timers'] as const;

export function useTaskTime(taskId: string | null): UseQueryResult<TaskTimeSummary, Error> {
  return useQuery({
    queryKey: [...queryKeys.task(taskId ?? 'none'), 'time'] as const,
    queryFn: () => http.get<TaskTimeSummary>(`/tasks/${taskId!}/time`),
    enabled: Boolean(taskId),
  });
}

/**
 * Every timer running for this person (#4 — there may be several). Oldest
 * first, decided by the server so the order does not shuffle as minutes tick.
 */
export function useTimers(): UseQueryResult<ActiveTimer[], Error> {
  return useQuery({
    queryKey: timerKey,
    queryFn: () => http.get<ActiveTimer[]>('/me/timers'),
    refetchInterval: 60_000,
  });
}

/** Is a timer running on this particular card? The card panel's question. */
export function useTimerFor(taskId: string): ActiveTimer | null {
  const { data } = useTimers();
  return data?.find((timer) => timer.taskId === taskId) ?? null;
}

export function useTimeMutations(taskId: string) {
  const queryClient = useQueryClient();

  const settle = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.task(taskId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.taskActivity(taskId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks }),
      queryClient.invalidateQueries({ queryKey: timerKey }),
    ]);
  };

  return {
    log: useMutation({
      mutationFn: (input: CreateTimeEntryRequest) =>
        http.post<TimeEntry>(`/tasks/${taskId}/time`, input),
      onSuccess: async (entry) => {
        await settle();
        toast.success(`Logged ${formatDuration(entry.minutes)}`);
      },
      onError: (error: Error) => toast.error(error.message),
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: UpdateTimeEntryRequest & { id: string }) =>
        http.patch<TimeEntry>(`/time/${id}`, input),
      onSuccess: settle,
      onError: (error: Error) => toast.error(error.message),
    }),
    remove: useMutation({
      mutationFn: (id: string) => http.delete<void>(`/time/${id}`),
      onSuccess: settle,
      onError: (error: Error) => toast.error(error.message),
    }),
  };
}

export function useTimerControls() {
  const queryClient = useQueryClient();

  const settle = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: timerKey }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks }),
    ]);
  };

  // #4 — no `stopRunning`, and no TIMER_ALREADY_RUNNING to swallow: starting a
  // second timer is simply allowed now, so the question the old flow asked has
  // no occasion to be asked.
  const start = useMutation({
    mutationFn: ({ taskId }: { taskId: string }) =>
      http.post<ActiveTimer>('/me/timer/start', { taskId }),
    onSuccess: settle,
    onError: (error: Error) => toast.error(error.message),
  });

  const stop = useMutation({
    mutationFn: ({ taskId, note }: { taskId: string; note?: string }) =>
      http.post<TimeEntry>('/me/timer/stop', { taskId, note: note ?? null }),
    onSuccess: async (entry) => {
      await settle();
      toast.success(`Logged ${formatDuration(entry.minutes)}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const discard = useMutation({
    mutationFn: ({ taskId }: { taskId: string }) =>
      http.post<void>('/me/timer/discard', { taskId }),
    onSuccess: async () => {
      await settle();
      toast.success('Timer discarded — nothing logged');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return { start, stop, discard };
}
